// Pure helpers for orders.pin_labels — the free-text labels drawn under an
// order's pin on the dispatch plan map.

/**
 * Longest allowed pin label, in characters. The cap lives here rather than in the database:
 * a Postgres CHECK constraint cannot unnest a text[]. See the order_pin_labels migration in onward-hasura.
 */
export const MAX_PIN_LABEL_LENGTH = 20;

/**
 * Trim a raw label, then cap it at `MAX_PIN_LABEL_LENGTH`. Trimming first keeps surrounding
 * whitespace from using up the cap.
 *
 * @param {*} raw User input; `null` and `undefined` become `''`.
 * @returns {string} The normalized label.
 */
export const normalizePinLabel = (raw) =>
    String(raw ?? '')
        .trim()
        .slice(0, MAX_PIN_LABEL_LENGTH);

/**
 * Add a normalized label unless it is blank or a case-insensitive duplicate. Returns `labels`
 * unchanged (same reference) when there is nothing to add, so a blur commit can skip the write
 * and not mark the edit modal dirty.
 *
 * @param {string[]|null|undefined} labels Current labels.
 * @param {*} raw User input.
 * @returns {string[]} A new array with the label appended, or the original `labels`.
 */
export const appendPinLabel = (labels, raw) => {
    const existing = labels || [];
    const next = normalizePinLabel(raw);

    if (!next) {
        return existing;
    }

    // Case-insensitive: "Fragile" and "fragile" would render as two identical
    // boxes under the pin, which reads as a bug rather than a distinction.
    const folded = next.toLowerCase();
    if (existing.some((label) => normalizePinLabel(label).toLowerCase() === folded)) {
        return existing;
    }

    return [...existing, next];
};

/**
 * Remove the label at `index`.
 *
 * @param {string[]|null|undefined} labels Current labels.
 * @param {number} index Position to remove.
 * @returns {string[]} A new array without that label.
 */
export const removePinLabel = (labels, index) => (labels || []).filter((_, i) => i !== index);
